import React, { useState, type FC, useContext, useEffect, useCallback, useRef } from 'react';
import Header from '../../components/header/header';
import styles from './pendingActions.module.scss';
import { useTranslation } from 'react-i18next';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { Button, Divider, FormControlLabel, List, ListItem, ListItemText, SvgIcon, Switch } from '@mui/material';
import { ReactComponent as notificationReadIcon } from '../../assets/notification_read.svg';
import ObrasAPI from '../../services/APIObras';

import { AuthContext } from '../../services/authContextProvider';
import { ReactComponent as tab_error } from '../../assets/tab_error.svg';

import { PendingActionType, ActionObra, ACTION_FILTER, ACTION_FILTER_SORT } from '../../types/PendingActionType';
import { ObraGeneralInfo } from '../../types/ObraDetails';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Comment from './components/comment/Comment';
import Error from '../../components/Error/Error';
import LoadingSpinner from '../../components/loadingSpinner/loadingSpinner';



// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface PendingActionsProps {
  defaultFilter?: string,
}

type PendingActionView = ActionObra & ObraGeneralInfo & {
  accion?: string;
};

const PendingActions: FC<PendingActionsProps> = (props: PendingActionsProps) => {
  const { defaultFilter } = props;
  const { t } = useTranslation();
  const api = ObrasAPI.getInstance();
  const authContext = useContext(AuthContext);
  const isAuthorized = authContext.canSystem('can_save_any_project') || authContext.canSystem('can_validate_any_project');
  const isValidator = authContext.canSystem('can_validate_any_project');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [pendingActions, setPendingActions] = useState<PendingActionView[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [countsLoading, setCountsLoading] = useState(false);
  const [countsError, setCountsError] = useState(false);
  const validatorFilters = isValidator ?
    {
      [ACTION_FILTER.VALIDATING]: 0,
      [ACTION_FILTER.FINAL_VALIDATING]: 0,
    } :
    {};
  const [countFilter, setCountFilter] = useState<Record<string, number>>({
    [ACTION_FILTER.ALL]: 0,
    [ACTION_FILTER.UPDATING]: 0,
    [ACTION_FILTER.VALIDATED]: 0,
    [ACTION_FILTER.COMMENTED]: 0,
    ...validatorFilters,
  });

  const [filter, setFilter] = useState(defaultFilter || searchParams.get('filter') || 'ALL');
  const [onlyPending, setOnlyPending] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const pageRequest = useRef<AbortController | null>(null);
  const loadPage = useCallback(async (cursor?: string, append = false, controller = new AbortController()) => {
    if (!isAuthorized) return;
    pageRequest.current?.abort();
    pageRequest.current = controller;
    setLoading(true);
    setError(false);
    try {
      const actions: PendingActionType = await api.getPendingActionsAPI(cursor, filter, onlyPending, controller.signal);
      if (controller.signal.aborted) return;
      setPendingActions(previous => append
        ? [...new Map([...previous, ...(actions.result.obras || [])].map(item => [item.codCentro, item])).values()]
        : actions.result.obras || []);
      setNextCursor(actions.result.nextCursor);
      setHasMore(actions.result.hasMore);
    } catch (e) {
      if (!controller.signal.aborted) setError(true);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
      if (pageRequest.current === controller) pageRequest.current = null;
    }
  }, [api, filter, onlyPending, isAuthorized]);

  useEffect(() => {
    const controller = new AbortController();
    setPendingActions([]);
    setNextCursor(null);
    setHasMore(false);
    if (isAuthorized) void loadPage(undefined, false, controller);
    return () => controller.abort();
  }, [isAuthorized, loadPage]);

  const loadCounts = useCallback((controller = new AbortController()) => {
    if (!isAuthorized) return;
    setCountsLoading(true);
    setCountsError(false);
    void api.getPendingActionCountsAPI(controller.signal)
      .then(counts => { if (!controller.signal.aborted) setCountFilter(previous => ({ ...previous, ...counts })); })
      .catch(() => { if (!controller.signal.aborted) setCountsError(true); })
      .finally(() => { if (!controller.signal.aborted) setCountsLoading(false); });
  }, [api, isAuthorized]);

  useEffect(() => {
    const controller = new AbortController();
    loadCounts(controller);
    return () => controller.abort();
  }, [loadCounts]);

  const close = () => {
    navigate(-1);
  };

  const handleFilter = (item: string) => {
    setFilter(item);
  };

  const handleChangePending = () => {
    setOnlyPending(!onlyPending);
  };

  const handleGoButton = (not: ActionObra & ObraGeneralInfo) => {
    api.setNotificationRead(not.notificacion).then(() => {

      navigate(`../${not.codCentro}/detalles`, {
        state: {
          userMode: 'editor',
        }
      });
      navigate(0);
    });
  };

  const parseDate = (date?: string) => {
    if (!date) return '';
    const d = new Date(date);
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
    return `${d.toLocaleDateString('es-es', options)} - ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  };

  const getActionComponent = (not: PendingActionView) => {
    // validated projects always have button active
    const enabled = not.pending || not.accion === 'project_validated';
    return <>
      <div className={styles.notificationItem}>
        <div className={styles.notificationHeader}>
          <div className={styles.notificationPoint}>
            {not.notificacion && !not.notificacion?.leido && <SvgIcon component={tab_error} inheritViewBox />}
          </div>
          <div className={styles.notificationDataCTI}>
            <div className={styles.notificationData}>
              <div className={styles.notificationName}>
                {`${not.nomCentro} - ${not.codCentro}`}
              </div>
              <div className={styles.notificationMotive}>
                {t(`actions.${not.accion}`)}
              </div>
              {/*  <div>{not.estadoPhia}</div>
              <div>{Object.keys(not.rolInProject).filter((r) => !!not.rolInProject[r]).join(',')}</div> */}
              <div className={styles.notificationDate}>
                {parseDate(not.fecModificacion)}
              </div>
            </div>
            <div className={styles.notificationCTI}>
              <Button variant='contained' className={styles.actionButton} disabled={!enabled}
                onClick={() => handleGoButton(not)}>
                {t(`actions.button.${not.accion}${!enabled ? '_disabled' : ''}`)}
              </Button>
            </div>
          </div>
        </div>
        {not.comentario &&
          <div className={styles.notificationComment}>
            <Comment comentario={not.comentario} />
          </div>}
      </div >

    </>;
  };

  return (
    <div data-testid="pendingActions-test" className='home' >
      <Header>
        <div id='pendingActions-header' className={styles.header}>
          <span onClick={close} className={styles.goBack}><ArrowBackIosIcon /></span>
          <div>{t('actions.title')}</div>
        </div>
      </Header>

      <div id='pendingActions-content' className={styles.pendingActionsContent}>
        <div className={styles.filtersContainer}>
          <div className={styles.filtersTitle}>{t('actions.filters.title')}</div>
          <List className={styles.filtersList} >
            {Object.keys(countFilter)
              .sort((a, b) => (ACTION_FILTER_SORT[a] - ACTION_FILTER_SORT[b]))
              .map((item: string, index) => {
                const title = t(`actions.filters.${item.toLowerCase()}`);
                return <ListItem button key={index + item} className={`${styles.filtersItem} ${filter === item && styles.filterSelected}`}
                  onClick={() => handleFilter(item)}>
                  <SvgIcon component={notificationReadIcon} inheritViewBox />
                  <ListItemText primary={`${title} (${countsLoading ? '…' : countFilter[item] ?? 0})`} />
                </ListItem>;
              })}
          </List>
          {countsError && <div role='alert'>
            {t('notifications.loadError')}
            <Button onClick={() => loadCounts()}>{t('notifications.retry')}</Button>
          </div>}
        </div>
        <div className={styles.resultsContainer}>
          <div className={styles.resultsHeader}>
            <FormControlLabel
              value='start'
              control={<Switch checked={onlyPending} onChange={handleChangePending}
                sx={{
                  '& .MuiSwitch-switchBase': {

                    '&.Mui-checked': {
                      color: '#fff',
                      '& + .MuiSwitch-track': {
                        opacity: 1,
                        backgroundColor: 'rgba(255, 0, 0, 1)',
                      },
                    }
                  }
                }} />}
              label={t('actions.pending')}
              labelPlacement='start'
            />
          </div>
          <div className={styles.resultsList} id='element'>
            {pendingActions?.length ?
              pendingActions.map((action: PendingActionView, i: number) => {
                return (<React.Fragment key={action.codCentro}>
                  {getActionComponent(action)}
                  {i < pendingActions.length - 1 && <Divider sx={{
                    margin: '0 !important'
                  }} />}
                </React.Fragment>);
              })
              :
              loading
                ? (<LoadingSpinner />)
                :
                <div className={styles.noResults}>{!loading && !error && t('actions.noResults')}</div>

            }
            {loading && pendingActions.length > 0 && <LoadingSpinner />}
            {error && <div role='alert'>
              <Error />
              <Button onClick={() => void loadPage(nextCursor || undefined, pendingActions.length > 0)}>{t('notifications.retry')}</Button>
            </div>}
            {!loading && !error && hasMore && nextCursor &&
              <Button onClick={() => void loadPage(nextCursor, true)}>{t('notifications.loadMore')}</Button>}
          </div>
        </div>
      </div>
    </div >

  );
};

export default PendingActions;
